// app.metrics — telemetry read STRAIGHT FROM THE BROWSER.
//
// The platform's thesis applied to observability: AWS is already collecting
// this, so the console asks CloudWatch with the operator's own scoped STS
// credentials — exactly as it asks S3. No metrics backend, no agent, no
// third party holding our users' traffic.
//
// Gated by the `observe` capability (owner/admin). A member gets AccessDenied,
// which is the correct answer.

import {
  CloudWatchClient,
  DescribeAlarmsCommand,
  GetMetricDataCommand,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-cloudwatch@3.1054.0/+esm";

export interface FunctionSeries {
  function: string;
  invocations: number;
  errors: number;
  throttles: number;
  /** Average duration in ms over the window (0 when never invoked). */
  avgDurationMs: number;
}

export interface AlarmState {
  name: string;
  state: "OK" | "ALARM" | "INSUFFICIENT_DATA" | string;
  reason?: string;
  description?: string;
}

/** One bucket's size, from CloudWatch's FREE daily storage metrics. */
export interface StorageSeries {
  bucket: string;
  /** Latest `BucketSizeBytes` (StandardStorage), or `null` when CloudWatch has
   *  no datapoint in the window.
   *
   *  `null` IS NOT ZERO. A bucket younger than a day, or one that has never
   *  held an object, has no datapoint at all — and "we don't know yet" must not
   *  render as "0 bytes", which reads as a healthy empty project. Same rule the
   *  Database panel follows for denied-vs-empty prefixes. */
  bytes: number | null;
  /** Latest `NumberOfObjects` (AllStorageTypes). Same null semantics. */
  objects: number | null;
  /** ISO timestamp of the datapoint used, or `null` when there is none. S3's
   *  storage metrics are emitted ONCE A DAY and lag ~24-48h — surfacing the
   *  timestamp is what keeps a stale number from reading as a live one. */
  at: string | null;
}

export interface MetricsModule {
  /** Per-function totals over the last `hours` (default 24). */
  functions(fnNames: string[], hours?: number): Promise<FunctionSeries[]>;
  /** Every alarm belonging to this project (plus platform-wide ones). */
  alarms(): Promise<AlarmState[]>;
  /** Size + object count for arbitrary buckets, newest datapoint within the
   *  last `days` (default 4 — comfortably wider than S3's ~48h publish lag).
   *
   *  PLATFORM-ONLY IN PRACTICE. `GetMetricData` takes no resource ARNs, so the
   *  `observe` capability grants it `Resource: "*"` on the ROOT project alone
   *  (see policy-templates.ts). A tenant calling this for its own bucket gets
   *  AccessDenied — correct, and the reason a tenant-facing usage panel has to
   *  go through a broker rather than reusing this. */
  storage(buckets: string[], days?: number): Promise<StorageSeries[]>;
}

export interface MetricsConfig {
  region: string;
  slug: string;
  getCredentials: () => Promise<{
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken?: string;
  }>;
}

export function createMetrics(cfg: MetricsConfig): MetricsModule {
  async function client(): Promise<CloudWatchClient> {
    const c = await cfg.getCredentials();
    return new CloudWatchClient({
      region: cfg.region,
      credentials: {
        accessKeyId: c.accessKeyId,
        secretAccessKey: c.secretAccessKey,
        sessionToken: c.sessionToken,
      },
    });
  }

  return {
    async functions(fnNames, hours = 24) {
      if (fnNames.length === 0) return [];
      const cw = await client();
      const end = new Date();
      const start = new Date(end.getTime() - hours * 3600_000);

      // One query per (function × metric). CloudWatch caps at 500 per call —
      // far more than any project has functions.
      const metrics = [
        ["invocations", "Invocations", "Sum"],
        ["errors", "Errors", "Sum"],
        ["throttles", "Throttles", "Sum"],
        ["duration", "Duration", "Average"],
      ] as const;
      const queries = fnNames.flatMap((fn, i) =>
        metrics.map(([key, name, stat]) => ({
          Id: `m${i}_${key}`,
          MetricStat: {
            Metric: {
              Namespace: "AWS/Lambda",
              MetricName: name,
              Dimensions: [{ Name: "FunctionName", Value: fn }],
            },
            Period: hours * 3600,
            Stat: stat,
          },
          ReturnData: true,
        })),
      );

      const res = await cw.send(
        new GetMetricDataCommand({
          StartTime: start,
          EndTime: end,
          MetricDataQueries: queries as never,
        }),
      );
      const val = (id: string) => {
        const r = (res.MetricDataResults ?? []).find((x: { Id?: string }) => x.Id === id);
        return r?.Values?.[0] ?? 0;
      };
      return fnNames.map((fn, i) => ({
        function: fn,
        invocations: val(`m${i}_invocations`),
        errors: val(`m${i}_errors`),
        throttles: val(`m${i}_throttles`),
        avgDurationMs: Math.round(val(`m${i}_duration`)),
      }));
    },

    async storage(buckets, days = 4) {
      if (buckets.length === 0) return [];
      const cw = await client();
      const end = new Date();
      const start = new Date(end.getTime() - days * 86_400_000);

      // GetMetricData caps at 500 queries per call and we spend 2 per bucket.
      // Chunking at 200 buckets keeps a 1,000-project fleet to 5 calls instead
      // of one that AWS rejects outright.
      const CHUNK = 200;
      const out: StorageSeries[] = [];

      for (let off = 0; off < buckets.length; off += CHUNK) {
        const slice = buckets.slice(off, off + CHUNK);
        // Storage metrics are per (bucket × StorageType) and the two we want use
        // DIFFERENT type dimensions: sizes are per storage class, counts are
        // only published as AllStorageTypes. Querying NumberOfObjects with
        // StandardStorage silently returns nothing.
        const queries = slice.flatMap((bucket, i) => [
          {
            Id: `b${i}_size`,
            MetricStat: {
              Metric: {
                Namespace: "AWS/S3",
                MetricName: "BucketSizeBytes",
                Dimensions: [
                  { Name: "BucketName", Value: bucket },
                  { Name: "StorageType", Value: "StandardStorage" },
                ],
              },
              Period: 86_400,
              Stat: "Average",
            },
            ReturnData: true,
          },
          {
            Id: `b${i}_objs`,
            MetricStat: {
              Metric: {
                Namespace: "AWS/S3",
                MetricName: "NumberOfObjects",
                Dimensions: [
                  { Name: "BucketName", Value: bucket },
                  { Name: "StorageType", Value: "AllStorageTypes" },
                ],
              },
              Period: 86_400,
              Stat: "Average",
            },
            ReturnData: true,
          },
        ]);

        const res = await cw.send(
          new GetMetricDataCommand({
            StartTime: start,
            EndTime: end,
            // Newest first, so Values[0] is the freshest datapoint rather than
            // the oldest one in the window.
            ScanBy: "TimestampDescending",
            MetricDataQueries: queries as never,
          }),
        );

        const pick = (id: string): { value: number | null; at: string | null } => {
          const r = (res.MetricDataResults ?? []).find((x: { Id?: string }) => x.Id === id);
          const value = r?.Values?.[0];
          const at = r?.Timestamps?.[0];
          return {
            value: typeof value === "number" ? value : null,
            at: at ? new Date(at).toISOString() : null,
          };
        };

        for (const [i, bucket] of slice.entries()) {
          const size = pick(`b${i}_size`);
          const objects = pick(`b${i}_objs`);
          out.push({
            bucket,
            bytes: size.value,
            objects: objects.value,
            at: size.at ?? objects.at,
          });
        }
      }
      return out;
    },

    async alarms() {
      const cw = await client();
      const out: AlarmState[] = [];
      for (const prefix of [`s3p-${cfg.slug}-`, "s3p-edge-", "s3p-ses-"]) {
        const res = await cw.send(new DescribeAlarmsCommand({ AlarmNamePrefix: prefix }));
        for (const a of res.MetricAlarms ?? []) {
          out.push({
            name: a.AlarmName ?? "?",
            state: a.StateValue ?? "INSUFFICIENT_DATA",
            reason: a.StateReason,
            description: a.AlarmDescription,
          });
        }
      }
      // De-dupe (a project prefixed alarm can match twice) and sort worst-first.
      const seen = new Set<string>();
      const rank = (s: string) => (s === "ALARM" ? 0 : s === "INSUFFICIENT_DATA" ? 2 : 1);
      return out
        .filter((a) => (seen.has(a.name) ? false : (seen.add(a.name), true)))
        .sort((a, b) => rank(a.state) - rank(b.state) || a.name.localeCompare(b.name));
    },
  };
}
